<template>
  <modal
    :name="nameProp"
    :adaptive="true"
    height="auto"
    :width="500"
    transition="linear"
    :delay="0.15"
    @before-close="beforModalClose"
  >
    <div class="fp-modal pay-expense-modal">
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        :is-full-page="fullPage">
      </loading>
      <form
        v-if="dataLoaded"
        name="payExpenseForm"
        novalidate
        @submit.prevent="onPayExpenseFormSubmitted"
        autocomplete="off"
      >
        <modals-header
          :modalNameProp="$lang.messages.payExpenseModal"
          @hideModalProp="hidePayExpenseModal"
        >
        </modals-header>

        <hr class="v--modal-hr">
        
        <div v-for="transaction in transactions" class="coparent-transaction">
          <div
              class="part"
          >
            <p class="label coparent-label">
              {{ transaction.coparentName }}
            </p>
          </div>
          
          <div
              v-if="transaction.spousalMaintenanceAmount !== 0"
              class="part"
          >
            <p class="label">
              {{ $lang.messages.spousalMaintenance }}
            </p>
            <p class="total-label">
              <currency-sign :currency="transaction.currency"></currency-sign>{{ currencyFormat(transaction.spousalMaintenanceAmount.toFixed(2)) }}
            </p>
          </div>
          
          <div
            class="part"
            v-if="transaction.childMaintenanceAmount !== 0"
          >
            <p class="label">
              {{ $lang.messages.childMaintenance }}
            </p>
            <p class="total-label">
              <currency-sign :currency="transaction.currency"></currency-sign>{{ currencyFormat(transaction.childMaintenanceAmount.toFixed(2)) }}
            </p>
          </div>

          <div
            v-if="transaction.sharedExpenseAmount !== 0"
            class="part"
          >
            <p class="label">
              {{ $lang.messages.sharedExpense }}
            </p>
            <p class="total-label">
              <currency-sign :currency="transaction.currency"></currency-sign>{{ currencyFormat(transaction.sharedExpenseAmount.toFixed(2)) }}
            </p>
          </div>
  
          <div class="part total-amount total-amount-by-coparent gray-part">
            <p class="label">
              {{ $lang.messages.total }}
            </p>
            <p class="total-label">
              <currency-sign :currency="transaction.currency"></currency-sign>{{currencyFormat((transaction.spousalMaintenanceAmount + transaction.childMaintenanceAmount + transaction.sharedExpenseAmount).toFixed(2)) }}
            </p>
          </div>
  
          <div class="part">
            <p class="label payment-method-label">
              {{ transaction.paymentType.toLowerCase() === 'ach' ? $lang.messages.paymentPreviewACH : $lang.messages.paymentPreviewDigitalCheck}}
            </p>
          </div>
        </div>
        
        <div class="part gray-part total-amount">
          <p class="label">
            {{ $lang.messages.fees }}
          </p>
          <p class="total-label">
            <currency-sign :currency="currency"></currency-sign>
            {{ this.fees.toFixed(2) }}
          </p>
          <p class="label">
            {{ $lang.messages.grandTotal }}
          </p>
          <p class="total-label">
            <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(totalAmount) }}
          </p>
        </div>
  
        <div class="part" v-if="expenses.length === 1 && transactions[0].paymentType.toLowerCase() === 'digital_check'">
          <p class="label">{{ $lang.messages.memo }}</p>
        </div>
  
        <div class="part" v-if="expenses.length === 1 && transactions[0].paymentType.toLowerCase() === 'digital_check'">
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('memo')}">
            <input
                type="text"
                name="memo"
                class="input"
                :placeholder="$lang.messages.enterMemo"
                v-model="memo"
                v-validate="'required|min:1|max:255'"
                @focus.self="clearInputErrors"
                @blur="clearOthers"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
            <div class="error-tooltip" v-show="errors.has('memo')" :class="{'hide-tooltip' : bluredProp !== 'memo'}">
              <div class="text">
                {{ errors.first('memo') }}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="generic-btn"
          @click="hidePayExpenseModal"
        >
          {{ $lang.messages.cancel }}
        </button>
        <button
          type="submit"
          class="action-btn"
          :class="{'disabled' : disablePayNow}"
          :disabled="disablePayNow"
        >
          <span v-show="!loading">{{ $lang.messages.payNow }}</span>
          <ClipLoader :loading="loading" :color="'#fff'" :size="'20px'"></ClipLoader>
        </button>
      </form>
    </div>
  </modal>
</template>

<script>
import { mapActions } from "vuex";
import { PREVIEW_EXPENSE, PAY_EXPENSE } from "@/store/action.types";
import Autocomplete from "@/components/common/Autocomplete";
import Loading from 'vue-loading-overlay';
import CurrencySign from "@/components/common/CurrencySign";
import modalsHeader from "@/components/common/modals/ModalsHeader";
import { currencyFormat } from "@/mixins/currencyFormat";
import ClipLoader from 'vue-spinner/src/ClipLoader';
import STATUS_CODES from "@/common/statusCodes";

export default {
  name: "payExpense",
  components: {
    Autocomplete,
    Loading,
    ClipLoader,
    CurrencySign,
    'modals-header': modalsHeader
  },
  mixins: [
    currencyFormat
  ],
  props: {
    nameProp: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dataLoaded: false,
      childMaintenanceTotal: 0,
      spousalMaintenanceTotal: 0,
      sharedExpensesTotal: 0,
      fees: 0,
      discount: 0,
      totalAmount: 0,
      expensesGuids: [],
      isLoading: true,
      fullPage: false,
      currency: '',
      disablePayNow: false,
      expenses: [],
      loading: false,
      transactions: [],
      memo: '',
      bluredProp: ''
    };
  },
  mounted() {
    this.disablePayNow = false;
  },
  methods: {
    ...mapActions({
      previewExpenseAction: PREVIEW_EXPENSE,
      payExpenseAction: PAY_EXPENSE
    }),
    /*
    * @description Call /preview endpoint and show the FPPayExpenseModal
    * @param {Object} - expensesFromPayments
    */
    show(expensesFromPayments) {
      this.expenses = expensesFromPayments;
      if (this.expenses.length === 1) {
        if (this.expenses[0].expenseType === this.$ALIMONY_EXPENSE_TYPE) {
          this.memo = this.$lang.messages.alimonyMemo + this.expenses[0].recipient.name;
        } else if (this.expenses[0].expenseType === this.$CHILD_EXPENSE_TYPE) {
          this.memo = this.$lang.messages.childSupportMemo + this.expenses[0].children;
        } else if (this.expenses[0].expenseType === this.$SHARED_EXPENSE_TYPE) {
          this.memo = this.expenses[0].description  + ' ' + this.$lang.messages.forLabel + ' ' + this.expenses[0].children;
        }
      }
      this.previewExpense();
      this.$modal.show(this.nameProp);
    },
    hidePayExpenseModal() {
      this.isLoading = true;
      this.clearPayExpenseData();
      this.$modal.hide(this.nameProp)
    },
    clearPayExpenseData() {
      this.childMaintenanceTotal = 0
      this.spousalMaintenanceTotal = 0
      this.sharedExpensesTotal = 0
      this.fees = 0
      this.discount = 0
      this.totalAmount = 0
      this.dataLoaded = false
    },
    // When user clicks on modal overlay destroy the data about expense preview
    beforModalClose() {
      this.isLoading = true;
      this.clearPayExpenseData();
    },
    previewExpense() {
      this.getExpensesGuids()
      this.previewExpenseAction({
        expensesGuids: this.expensesGuids
      })
      .then(response => {
        this.transactions = response.data.data.coparentTransferAmountsDtoList;
        response.data.data.coparentTransferAmountsDtoList.forEach(element => {
          this.totalAmount += element.spousalMaintenanceAmount;
          this.totalAmount += element.childMaintenanceAmount;
          this.totalAmount += element.sharedExpenseAmount;
          this.currency = element.currency;
        });
        this.fees = response.data.data.fees;
        this.totalAmount += this.fees;
      
            this.isLoading = false;
        this.dataLoaded = true;
      })
      .catch(error => {
        if (error.response.status !== 406) {
          this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
        }
      });
    },
    getExpensesGuids() {
      this.expensesGuids = []
      this.expenses.forEach(element => {
        if (
            ( element.paymentStatus && element.paymentStatus === 'NOT_PAID' ) ||
            ( element.status && element.status === 'NOT_PAID' )||
            ( element.payments && element.payments[0] && element.payments[0].paymentStatus === 'NOT_PAID' ) ||
            ( element.reimbursementPayment && element.reimbursementPayment.paymentStatus && element.reimbursementPayment.paymentStatus === 'NOT_PAID' ) ||
            ( element.paymentStatus && element.paymentStatus === 'REJECTED' ) ||
            ( element.status && element.status === 'REJECTED' )||
            ( element.payments && element.payments[0] && element.payments[0].paymentStatus === 'REJECTED' ) ||
            ( element.reimbursementPayment && element.reimbursementPayment.paymentStatus && element.reimbursementPayment.paymentStatus === 'REJECTED' )
        ) {
          if (element.guid) {
            // If user clicks on the same expense twice,
            // don't push the same guid into the array
            if (this.expensesGuids.includes(element.guid)) {
              this.isLoading = true
              return;
            }
            this.expensesGuids.push(element.guid)
          } else if (element.expenseGuid) {
            if (this.expensesGuids.includes(element.expenseGuid)) {
              this.isLoading = true
              return;
            }
            this.expensesGuids.push(element.expenseGuid)
          }
        }
      });
    },
    onPayExpenseFormSubmitted() {
      this.$validator.validateAll().then(result => {
        if (result) {
          if (!this.loading) {
            this.loading = true;
            this.disablePayNow = true;
            this.payExpenseAction({
              expensesGuids: this.expensesGuids,
              memo: this.expenses.length === 1 ? this.memo : null
            })
            .then(() => {
              this.loading = false;
              this.disablePayNow = false;
              this.hidePayExpenseModal();
              this.loadExpenses();
              this.expensesGuids = [];
              this.$emit('clearPayExpenseData');
              this.$snotify.success(this.$lang.messages.sn_succesPaidExpense, "SUCCESS");
            })
            .catch(error => {
              this.hidePayExpenseModal()
              if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                this.$snotify.error(error.response.data.status.desc, "ERROR");
              }
            });
          }
        }
      })
    },
    loadExpenses() {
      this.$emit("loadExpenses", this.expensesGuids[0]);
    },
    clearInputErrors(e) {
      this.$validator.errors.remove(e.target.name);
    },
    checkAnyInput() {
      this.bluredProp = '';
    },
    clearOthers(e) {
      this.bluredProp = e.target.name;
    },
  }
};
</script>
